<template>
  <div class="fill-height">
    <admin-wrapper
      title-translation-path="admin.superadmin"
      :has-buttons="false"
      :is-super-admin="true"
      :inputs="computedModules"
      :is-fetched="isFetched"
      @change="handleModuleChange"
      hide-title-padding
    >
      <template #icon="{name, verbose_name}">
        <v-col cols="auto" class="ml-2">
          <v-tooltip color="font" right>
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" icon @click="openChangeModuleNameDialog(name, verbose_name)">
                <v-icon color="font">mdi-pencil</v-icon>
              </v-btn>
            </template>
            <span> {{ $i18n.t('tooltips.editModuleName') }}</span>
          </v-tooltip>
        </v-col>
      </template>
      <template #top>
        <super-admin-limitations-settings class="pt-8" />
        <v-row no-gutters class="pt-8">
          <v-col cols="auto" class="font-weight-medium font--text">
            {{ $i18n.t('navbar.additionalModules') }}
          </v-col>
          <v-spacer />
          <v-col cols="2">
            <data-input
              clearable
              hide-details
              :placeholder="$i18n.t('default.find')"
              prepend-inner-icon="mdi-magnify"
              :data-type="{ name: 'text' }"
              v-model="filterValue"
              @input="filter"
              class="search-filter-value"
            />
          </v-col>
        </v-row>
      </template>
      <template #[`AQUAGIS_MOBILE.prepend-bottom`]>
        <super-admin-upload-row
          :is-enabled="isMobileEnabled"
          :added="hasMobileApp ? hasMobileApp : false"
          :delete-loading="deleteLoading"
          :data-input="computedInputMobile"
          :version="aquagisMobileAppVersion"
          :date-uploaded="aquagisMobileAppDateUploaded"
          :has-delete-button="true"
          @showUpload="showMobileAppUploadDialog('aquagis')"
          @deleteFile="handleDeleteAppMobile('aquagis')"
        >
        </super-admin-upload-row>
      </template>
      <template #[`AQUAGIS_OFFLINE_MOBILE.bottom`]>
        <super-admin-upload-row
          :is-enabled="isMobileOfflineEnabled"
          :added="hasOfflineMobileApp ? hasOfflineMobileApp : false"
          :delete-loading="deleteLoading"
          :data-input="computedInputOfflineMobile"
          :version="aquagisOfflineMobileAppVersion"
          :date-uploaded="aquagisOfflineMobileAppDateUploaded"
          :has-delete-button="true"
          @showUpload="showMobileAppUploadDialog('aquagis_offline')"
          @deleteFile="handleDeleteAppMobile('aquagis_offline')"
        >
        </super-admin-upload-row>
      </template>
      <template #[`ZDM_MOBILE.bottom`]>
        <super-admin-upload-row
          :is-enabled="isZDMMobileEnabled"
          :added="hasZDMMobileApp ? hasZDMMobileApp : false"
          :delete-loading="deleteLoading"
          :data-input="computedInputZDMMobile"
          :version="ZDMMobileAppVersion"
          :date-uploaded="ZDMMobileAppDateUploaded"
          :has-delete-button="true"
          @showUpload="showMobileAppUploadDialog('zdm')"
          @deleteFile="handleDeleteAppMobile('zdm')"
        >
        </super-admin-upload-row>
      </template>
      <template #[`QGIS_PLUGIN.bottom`]>
        <qgis-plugin-admin />
      </template>
      <template #[`INFO.bottom`]>
        <information-admin :disabled="!isInformationEnabled"></information-admin>
      </template>
      <template #[`BILLINGS.prepend-bottom`]>
        <v-col cols="auto" class="px-0 pt-6" align="left">
          <base-button
            translation-path="button.billingForceMigration"
            @click="forceBillingMigration"
            :loading="isForcedBillingMigrationLoading"
          ></base-button>
        </v-col>
      </template>
      <template #[`PUBLIC_TOKENS.bottom`]>
        <v-col v-if="isPublicTokensEnabled" cols="6" class="pa-0 pt-6">
          <public-tokens />
        </v-col>
      </template>
      <template
        v-for="(subSettingsModule, idx) of Object.keys(this.modulesSubSettings)"
        #[`${subSettingsModule}.bottom`]
      >
        <super-admin-module-sub-settings
          :key="`ss-${idx}`"
          :settings="modulesSubSettings[subSettingsModule]"
          :module="subSettingsModule"
        />
      </template>
      <template v-for="(settingsModule, idx) of Object.keys(this.modulesSettings)" #[`${settingsModule}.append-bottom`]>
        <super-admin-module-settings
          :key="`s-${idx}`"
          :settings="modulesSettings[settingsModule]"
          :module="settingsModule"
          @editSingleSetting="editSingleSettingHandler"
        />
      </template>
    </admin-wrapper>
    <main-large-dialog
      v-if="isLoaded"
      :is-visible.sync="isVisible"
      :title="$i18n.t('admin.newAPK')"
      agree-text="admin.upload"
      disagree-text="dialog.close"
      @agree="uploadMobile"
      dialog-width="28%"
    >
      <template #body>
        <admin-form :inputs="dialogInputsMobile(activeUpload)" class="px-8 pb-10">
          <template #[`file.input`]="{dropzoneOptions}">
            <v-col cols="6">
              <file-input
                @successfullyAdded="successfullyAddedMobile"
                ref="fileMobile"
                :options="dropzoneOptions"
              ></file-input>
            </v-col>
          </template>
        </admin-form>
      </template>
    </main-large-dialog>
    <main-input-dialog
      v-if="isChangeModuleNameLoaded"
      :is-visible.sync="isChangeModuleNameVisible"
      :title-translation="$i18n.t('dialog.title.changeModuleName')"
      agree-text="dialog.agreeSave"
      :inputs="changeModuleNameInput"
      :loading="isLoading"
      body-height="120px"
      @agree="changeModuleNameHandler"
    >
      <template #[`changed_module_name.input`]="data">
        <v-col cols="6">
          <data-input v-model="changedModuleName" v-bind="data" />
        </v-col>
      </template>
    </main-input-dialog>
  </div>
</template>

<script>
import { call, get } from 'vuex-pathify';
import { cloneDeep, debounce } from 'lodash';

import AdminWrapper from '@/components/AdminWrapper';
import AdminForm from '@/components/AdminForm';
import FileInput from '@/components/FileInput';
import SuperAdminUploadRow from '@/components/SuperAdminUploadRow';
import InformationAdmin from '@/components/InformationAdmin';
import DataInput from '@/components/DataInput';
import SuperAdminModuleSubSettings from '@/components/SuperAdminModuleSubSettings';
import SuperAdminModuleSettings from '@/components/SuperAdminModuleSettings';
import QgisPluginAdmin from '@/components/QgisPluginAdmin';
import PublicTokens from '@/components/PublicTokens';
import SuperAdminLimitationsSettings from '@/components/SuperAdminLimitationsSettings';

import modulesSubSettings from '@/mixins/modulesSubSettings';

export default {
  name: 'Superadmin',
  mixins: [modulesSubSettings],
  components: {
    InformationAdmin,
    SuperAdminUploadRow,
    AdminWrapper,
    MainLargeDialog: () => import('@/components/MainLargeDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
    AdminForm,
    FileInput,
    DataInput,
    SuperAdminModuleSubSettings,
    SuperAdminModuleSettings,
    QgisPluginAdmin,
    PublicTokens,
    SuperAdminLimitationsSettings,
  },
  data: () => ({
    deleteLoading: false,
    isFetched: false,
    activeUpload: '',
    values: [],
    isVisible: false,
    isLoaded: false,
    isLoading: false,
    isChangeModuleNameVisible: false,
    isChangeModuleNameLoaded: false,
    isForcedBillingMigrationLoading: false,
    moduleName: '',
    changedModuleName: '',
    computedModules: [],
    filterValue: '',
  }),
  computed: {
    aquagisMobileAppVersion: get('admin/mobileAppMetadata@aquagis.version'),
    aquagisOfflineMobileAppVersion: get('admin/mobileAppMetadata@aquagis_offline.version'),
    ZDMMobileAppVersion: get('admin/mobileAppMetadata@zdm.version'),
    aquagisMobileAppDateUploaded: get('admin/mobileAppMetadata@aquagis.date_uploaded'),
    aquagisOfflineMobileAppDateUploaded: get('admin/mobileAppMetadata@aquagis_offline.date_uploaded'),
    ZDMMobileAppDateUploaded: get('admin/mobileAppMetadata@zdm.date_uploaded'),
    hasMobileApp: get('admin/hasMobileApp@aquagis'),
    hasOfflineMobileApp: get('admin/hasMobileApp@aquagis_offline'),
    hasZDMMobileApp: get('admin/hasMobileApp@zdm'),
    additionalModules: get('admin/additionalModules'),
    isMobileEnabled: get('admin/additionalModules@AQUAGIS_MOBILE.enabled'),
    isMobileOfflineEnabled: get('admin/additionalModules@AQUAGIS_OFFLINE_MOBILE.enabled'),
    isZDMMobileEnabled: get('admin/additionalModules@ZDM_MOBILE.enabled'),
    isInformationEnabled: get('admin/additionalModules@INFO.enabled'),
    isPublicTokensEnabled: get('admin/additionalModules@PUBLIC_TOKENS.enabled'),
    computedInputMobile() {
      if (this.hasMobileApp) {
        return {
          name: 'upload',
          translationPath: 'admin.apkVersion',
          isCustom: true,
          isSuperadmin: true,
          isSubsection: true,
        };
      }
      return {
        name: 'upload',
        translationPath: 'admin.uploadMobile',
        isCustom: true,
        align: 'baseline',
        subtitle: 'admin.uploadMobileSubtitle',
        isSuperadmin: true,
      };
    },
    computedInputOfflineMobile() {
      if (this.hasOfflineMobileApp) {
        return {
          name: 'upload',
          translationPath: 'admin.apkVersion',
          isCustom: true,
          isSuperadmin: true,
          isSubsection: true,
        };
      }
      return {
        name: 'upload',
        translationPath: 'admin.uploadMobile',
        isCustom: true,
        align: 'baseline',
        subtitle: 'admin.uploadMobileSubtitle',
        isSuperadmin: true,
      };
    },
    computedInputZDMMobile() {
      if (this.hasZDMMobileApp) {
        return {
          name: 'upload',
          translationPath: 'admin.apkVersion',
          isCustom: true,
          isSuperadmin: true,
          isSubsection: true,
        };
      }
      return {
        name: 'upload',
        translationPath: 'admin.uploadMobile',
        isCustom: true,
        align: 'baseline',
        subtitle: 'admin.uploadMobileSubtitle',
        isSuperadmin: true,
      };
    },
    changeModuleNameInput() {
      return [
        {
          name: 'changed_module_name',
          translationPath: 'dialog.name',
          dataType: { name: 'text' },
          cols: 12,
          leftCol: 5,
          rightCol: 6,
          spacer: true,
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
      ];
    },
  },
  watch: {
    isMobileEnabled(nV) {
      this.onModuleToggled(nV, 'getMobileMetadata', 'aquagis');
    },
    isMobileOfflineEnabled(nV) {
      this.onModuleToggled(nV, 'getMobileMetadata', 'aquagis_offline');
    },
    isZDMMobileEnabled(nV) {
      this.onModuleToggled(nV, 'getMobileMetadata', 'zdm');
    },
  },
  methods: {
    editSingleSetting: call('admin/editSingleSetting'),
    editSingleSettingHandler(value, name) {
      this.editSingleSetting({
        key: name,
        body: {
          value,
        },
      });
    },
    getMobileMetadata: call('admin/getMobileMetadata'),
    deleteMobileApp: call('admin/deleteMobileApp'),
    setAdditionalModuleValue: call('admin/setAdditionalModuleValue'),
    changeModuleName: call('admin/changeAdditionalModuleName'),
    migrateBillingsData: call('billings/migrateBillingsData'),
    dialogInputsMobile(value) {
      if (value === 'aquagis') {
        return [
          {
            name: 'file',
            translationPath: 'admin.file',
            align: 'start',
            isCustom: true,
            dropzoneOptions: {
              addRemoveLinks: true,
              url: `${process.env.VUE_APP_API_URL}/mobile_app/aquagis`,
              method: 'post',
              autoProcessQueue: false,
              paramName: 'file',
              acceptedFiles: '.apk',
              maxFiles: 1,
            },
          },
        ];
      } else if (value === 'aquagis_offline') {
        return [
          {
            name: 'file',
            translationPath: 'admin.file',
            align: 'start',
            isCustom: true,
            dropzoneOptions: {
              addRemoveLinks: true,
              url: `${process.env.VUE_APP_API_URL}/mobile_app/aquagis_offline`,
              method: 'post',
              autoProcessQueue: false,
              paramName: 'file',
              acceptedFiles: '.apk',
              maxFiles: 1,
            },
          },
        ];
      } else if (value === 'zdm') {
        return [
          {
            name: 'file',
            translationPath: 'admin.file',
            align: 'start',
            isCustom: true,
            dropzoneOptions: {
              addRemoveLinks: true,
              url: `${process.env.VUE_APP_API_URL}/mobile_app/zdm`,
              method: 'post',
              autoProcessQueue: false,
              paramName: 'file',
              acceptedFiles: '.apk',
              maxFiles: 1,
            },
          },
        ];
      }
    },
    async handleGetMobileMetadata(app_kind) {
      await this.getMobileMetadata(app_kind);
    },
    async handleDeleteAppMobile(app_kind) {
      try {
        this.deleteLoading = true;
        await this.deleteMobileApp(app_kind);
        this.deleteLoading = false;
      } catch (e) {
        this.deleteLoading = false;
      }
    },
    async handleModuleChange(enabled, name) {
      await this.setAdditionalModuleValue({ name, enabled });
      this.$store.set('admin/SET_MODULE_VALUE!', { name, enabled });
    },
    async successfullyAddedMobile() {
      this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
        message: this.$i18n.t('success.apk'),
      });
      this.isVisible = false;
      this.isLoaded = false;
      this.$refs.fileMobile.$refs.dropzone.removeAllFiles();
      await this.getMobileMetadata(this.activeUpload);
      this.activeUpload = '';
    },
    showMobileAppUploadDialog(value) {
      this.activeUpload = value;
      this.isVisible = true;
      this.isLoaded = true;
    },
    uploadMobile() {
      this.$refs.fileMobile.$refs.dropzone.processQueue();
    },
    onModuleToggled(nV, method, parameter) {
      if (nV) {
        this[method](parameter);
      }
    },
    async fetch() {
      const promises = [];
      if (this.isMobileEnabled) {
        promises.push(this.getMobileMetadata('aquagis'));
      }
      if (this.isZDMMobileEnabled) {
        promises.push(this.getMobileMetadata('zdm'));
      }
      if (this.isMobileOfflineEnabled) {
        promises.push(this.getMobileMetadata('aquagis_offline'));
      }
      await Promise.all(promises);
      this.isFetched = true;
    },
    filter() {
      this.computedModules = this.getComputedModules();
    },
    getComputedModules() {
      return Object.keys(this.additionalModules)
        .reduce((total, current) => {
          return [
            ...total,
            {
              ...this.additionalModules[current],
              hasSuffix: false,
              storeSubproperty: 'additionalModules',
              valueSubproperty: 'enabled',
              isSuperadmin: true,
              straightText: this.additionalModules[current]?.verbose_name,
              dataType: {
                name: 'switch',
              },
              subtitle: this.$i18n.t('admin.moduleEnabler', {
                suffix: this.additionalModules[current]?.verbose_name,
              }),
            },
          ];
        }, [])
        .filter(computedModule => {
          return computedModule?.verbose_name
            .toLocaleLowerCase()
            .includes((this.filterValue || '').toLocaleLowerCase());
        })
        .sort((a, b) => {
          return a.straightText.localeCompare(b.straightText);
        });
    },
    openChangeModuleNameDialog(name, verbose_name) {
      this.moduleName = name;
      this.changedModuleName = verbose_name;
      this.isChangeModuleNameVisible = true;
      this.isChangeModuleNameLoaded = true;
    },
    async changeModuleNameHandler() {
      try {
        this.isLoading = true;
        const payload = {
          name: this.moduleName,
          data: {
            verbose_name: this.changedModuleName,
          },
        };
        await this.changeModuleName(payload);
        this.filter();
      } finally {
        this.isLoading = false;
        this.isChangeModuleNameVisible = false;
        this.isChangeModuleNameLoaded = false;
      }
    },
    async forceBillingMigration() {
      this.isForcedBillingMigrationLoading = true;
      try {
        await this.migrateBillingsData();
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.successBillingMigration'),
        });
      } finally {
        this.isForcedBillingMigrationLoading = false;
      }
    },
  },
  created() {
    this.filter = debounce(this.filter, 500);
    this.computedModules = this.getComputedModules();
    this.values = cloneDeep(this.additionalModules);
    this.fetch();
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .search-filter-value {
    .v-input__slot {
      min-height: 0px !important;
      padding: 0 4px !important;
    }
    .v-input__append-inner {
      margin: 6px !important;
    }
    .v-input__prepend-inner {
      .v-input__icon {
        .v-icon {
          font-size: 16px;
          padding-bottom: 5px;
        }
      }
    }
    .v-text-field__slot input {
      font-size: 12px !important;
    }
  }
}
</style>
